import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { GetAllGGNotificationGraspDTO, GetAllGGNotificationGraspFiltersDTO, GGNotificationGraspDTO } from "./dataservice/dto";
import { NewGGNotificationGraspService } from "./dataservice/service";

interface GGNotificationGraspState {
    // REQUEST
    graspUpdateRequest: GGNotificationGraspDTO
    graspIdToUpdate: string

    // FILTERS
    graspFilters: GetAllGGNotificationGraspFiltersDTO

    // STATUS
    graspUpdateStatus: PromiseStatuses
    getAllGraspStatus: PromiseStatuses
    getGraspByIdStatus: PromiseStatuses

    // RESPONSE
    getAllGraspResponse?: GetAllGGNotificationGraspDTO
    getGraspByIdResponse?: GGNotificationGraspDTO
}

const initialState: GGNotificationGraspState = {
    graspUpdateRequest: {},
    graspIdToUpdate: '',

    graspFilters: {
        itemsPerPage: 0,
        page: 0
    },

    graspUpdateStatus: 'idle',
    getAllGraspStatus: 'idle',
    getGraspByIdStatus: 'idle'
}

export const GGNotificationGraspUpdate = createAsyncThunk(
    'GGNotificationGrasp/Update',
    async (request: { body: GGNotificationGraspDTO, id: string }, thunkApi): Promise<void> => {
        const GGNotificationGraspService = NewGGNotificationGraspService()
        return GGNotificationGraspService.UpdateGGNotificationGrasp(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllGGNotificationGrasp = createAsyncThunk(
    'GGNotificationGrasp/GetAll',
    async (filters: GetAllGGNotificationGraspFiltersDTO, thunkApi): Promise<GetAllGGNotificationGraspDTO> => {
        const GGNotificationGraspService = NewGGNotificationGraspService()
        return GGNotificationGraspService.GetAllGGNotificationGrasp(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetGGNotificationGraspById = createAsyncThunk(
    'GGNotificationGrasp/GetById',
    async (id: string, thunkApi): Promise<GGNotificationGraspDTO> => {
        const GGNotificationGraspService = NewGGNotificationGraspService()
        return GGNotificationGraspService.GetGGNotificationGraspById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const GGNotificationGraspSlice = createSlice({
    name: 'grasp/slice',
    initialState,
    reducers: {
        // REQUEST
        setGraspUpdateRequest: (state, action) => {
            state.graspUpdateRequest = action.payload
        },
        setGraspIdToUpdate: (state, action) => {
            state.graspIdToUpdate = action.payload
        },
        setGraspUpdateFamilyWork: (state, action) => {
            state.graspUpdateRequest.familyWork = action.payload
        },
        setGraspUpdateFarmPermanentNationalWorker: (state, action) => {
            state.graspUpdateRequest.farmPermanentNationalWorker = action.payload
        },
        setGraspUpdateFarmTemporaryNationalWorker: (state, action) => {
            state.graspUpdateRequest.farmTemporaryNationalWorker = action.payload
        },
        setGraspUpdateFarmPermanentForeignWorker: (state, action) => {
            state.graspUpdateRequest.farmPermanentForeignWorker = action.payload
        },
        setGraspUpdateFarmTemporaryForeignWorker: (state, action) => {
            state.graspUpdateRequest.farmTemporaryForeignWorker = action.payload
        },
        setGraspUpdateManipulationPermanentNationalWorker: (state, action) => {
            state.graspUpdateRequest.manipulationPermanentNationalWorker = action.payload
        },
        setGraspUpdateManipulationTemporaryNationalWorker: (state, action) => {
            state.graspUpdateRequest.manipulationTemporaryNationalWorker = action.payload
        },
        setGraspUpdateManipulationPermanentForeignWorker: (state, action) => {
            state.graspUpdateRequest.manipulationPermanentForeignWorker = action.payload
        },
        setGraspUpdateManipulationTemporaryForeignWorker: (state, action) => {
            state.graspUpdateRequest.manipulationTemporaryForeignWorker = action.payload
        },
        resetGraspUpdateRequest: (state) => {
            state.graspUpdateRequest = {}
        },

        // FILTERS
        setGraspFilterItemsPerPage: (state, action) => {
            state.graspFilters.itemsPerPage = action.payload
        },
        setGraspFilterPage: (state, action) => {
            state.graspFilters.page = action.payload
        },
        setGraspFilterGGNotificationId: (state, action) => {
            state.graspFilters.ggNotificationId = action.payload
        },
        resetGraspFilters: (state) => {
            state.graspFilters = {
                itemsPerPage: 0,
                page: 0
            }
        },

        // STATUS
        resetGraspUpdateStatus: (state) => {
            state.graspUpdateStatus = 'idle'
        },
        resetGetAllGraspStatus: (state) => {
            state.getAllGraspStatus = 'idle'
        },
        resetGetGraspByIdStatus: (state) => {
            state.getGraspByIdStatus = 'idle'
        }
    },
    extraReducers(builder) {
        builder

            // GET ALL
            .addCase(GetAllGGNotificationGrasp.pending, (state) => {
                state.getAllGraspStatus = 'loading'
            })
            .addCase(GetAllGGNotificationGrasp.fulfilled, (state, action) => {
                state.getAllGraspStatus = 'successfully'
                state.getAllGraspResponse = action.payload
            })
            .addCase(GetAllGGNotificationGrasp.rejected, (state, action) => {
                state.getAllGraspStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetGGNotificationGraspById.pending, (state) => {
                state.getGraspByIdStatus = 'loading'
            })
            .addCase(GetGGNotificationGraspById.fulfilled, (state, action) => {
                state.getGraspByIdStatus = 'successfully'
                state.getGraspByIdResponse = action.payload
            })
            .addCase(GetGGNotificationGraspById.rejected, (state, action) => {
                state.getGraspByIdStatus = 'failed'
            })

            // UPDATE
            .addCase(GGNotificationGraspUpdate.pending, (state) => {
                state.graspUpdateStatus = 'loading'
            })
            .addCase(GGNotificationGraspUpdate.fulfilled, (state) => {
                state.graspUpdateStatus = 'successfully'
            })
            .addCase(GGNotificationGraspUpdate.rejected, (state, action) => {
                state.graspUpdateStatus = 'failed'
            })
    },
})

export const {
    setGraspUpdateRequest,
    setGraspIdToUpdate,
    setGraspFilterGGNotificationId,
    setGraspFilterItemsPerPage,
    setGraspFilterPage,
    setGraspUpdateFamilyWork,
    setGraspUpdateFarmPermanentForeignWorker,
    setGraspUpdateFarmPermanentNationalWorker,
    setGraspUpdateFarmTemporaryForeignWorker,
    setGraspUpdateFarmTemporaryNationalWorker,
    setGraspUpdateManipulationPermanentForeignWorker,
    setGraspUpdateManipulationPermanentNationalWorker,
    setGraspUpdateManipulationTemporaryForeignWorker,
    setGraspUpdateManipulationTemporaryNationalWorker,
    resetGetAllGraspStatus,
    resetGetGraspByIdStatus,
    resetGraspFilters,
    resetGraspUpdateRequest,
    resetGraspUpdateStatus
} = GGNotificationGraspSlice.actions

export default GGNotificationGraspSlice.reducer